import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Filter, CreditCard, Calendar,
  CheckCircle, Banknote, Building2, FileText,
  ChevronDown, X, Loader2, MoreVertical,
  Trash2, Eye, RefreshCw, Plus, PoundSterling,
  ArrowUpDown, Clock
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getMethodConfig = (method) => {
  const configs = {
    stripe: { label: 'Card (Stripe)', color: 'bg-[#635BFF] text-white', Icon: CreditCard },
    card: { label: 'Card', color: 'bg-blue-500 text-white', Icon: CreditCard },
    gocardless: { label: 'Direct Debit', color: 'bg-[#00D9C6] text-white', Icon: Building2 },
    direct_debit: { label: 'Direct Debit', color: 'bg-[#00D9C6] text-white', Icon: Building2 },
    cash: { label: 'Cash', color: 'bg-green-100 text-green-700', Icon: Banknote },
    bank_transfer: { label: 'Bank Transfer', color: 'bg-purple-100 text-purple-700', Icon: Building2 },
    bacs: { label: 'BACS', color: 'bg-indigo-100 text-indigo-700', Icon: Building2 },
    standing_order: { label: 'Standing Order', color: 'bg-orange-100 text-orange-700', Icon: RefreshCw },
    cheque: { label: 'Cheque', color: 'bg-amber-100 text-amber-700', Icon: FileText },
    other: { label: 'Other', color: 'bg-gray-100 text-gray-700', Icon: PoundSterling },
  };
  return configs[method] || configs.other;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const PaymentCard = ({ payment, onView, onCancel }) => {
  const methodConfig = getMethodConfig(payment.payment_method);
  const MethodIcon = methodConfig.Icon;
  const isCancelled = payment.status === 'cancelled';
  
  return (
    <div 
      data-testid={`payment-card-${payment.id}`}
      className={`bg-white border-b border-gray-100 p-4 hover:bg-gray-50 transition-colors ${isCancelled ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{payment.customer_name}</h3>
            <Badge className={methodConfig.color}>
              <MethodIcon size={12} className="mr-1" />
              {methodConfig.label}
            </Badge>
            {isCancelled && (
              <Badge variant="destructive">Cancelled</Badge>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
            <div className="flex items-center gap-1">
              <Calendar size={14} />
              <span>{formatDate(payment.payment_date || payment.created_at)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{formatTime(payment.created_at)}</span>
            </div>
            {payment.reference && (
              <span className="text-gray-400 truncate">{payment.reference}</span>
            )}
          </div>
          
          {payment.notes && (
            <p className="text-sm text-gray-500 mt-1 truncate">{payment.notes}</p>
          )}
        </div>
        
        <div className="flex items-center gap-2 ml-4">
          <div className="text-right">
            <p className={`text-xl font-semibold ${isCancelled ? 'text-gray-400 line-through' : 'text-green-600'}`}>
              +£{payment.amount?.toFixed(2)}
            </p>
            {!isCancelled && (
              <div className="flex items-center gap-1 text-xs text-green-600">
                <CheckCircle size={12} />
                <span>Completed</span>
              </div>
            )}
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`payment-menu-${payment.id}`}>
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(payment)}>
                <Eye size={16} className="mr-2" />
                View Details
              </DropdownMenuItem>
              {!isCancelled && (
                <DropdownMenuItem 
                  onClick={() => onCancel(payment)}
                  className="text-red-600"
                >
                  <Trash2 size={16} className="mr-2" />
                  Cancel Payment
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const PaymentDetailModal = ({ payment, isOpen, onClose }) => {
  if (!payment) return null;
  
  const methodConfig = getMethodConfig(payment.payment_method);
  const MethodIcon = methodConfig.Icon;
  const isCancelled = payment.status === 'cancelled';
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="payment-detail-modal">
        <DialogHeader>
          <DialogTitle>Payment Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Amount</span>
            <span className={`text-2xl font-bold ${isCancelled ? 'text-gray-400 line-through' : 'text-green-600'}`}>
              £{payment.amount?.toFixed(2)}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Status</span>
            {isCancelled ? (
              <Badge variant="destructive">Cancelled</Badge>
            ) : (
              <Badge className="bg-green-100 text-green-700">Completed</Badge>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Payment Method</span>
            <Badge className={methodConfig.color}>
              <MethodIcon size={12} className="mr-1" />
              {methodConfig.label}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Customer</span>
            <span className="font-medium">{payment.customer_name}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Date</span>
            <span>{formatDate(payment.payment_date || payment.created_at)}</span>
          </div>
          
          {payment.reference && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Reference</span>
              <span className="font-mono text-sm">{payment.reference}</span>
            </div>
          )}
          
          {payment.recorded_by && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Recorded By</span>
              <span className="text-sm">{payment.recorded_by}</span>
            </div>
          )}
          
          {payment.notes && (
            <div>
              <span className="text-sm text-gray-500">Notes</span>
              <p className="mt-1 text-sm bg-gray-50 p-2 rounded">{payment.notes}</p>
            </div>
          )}
          
          {isCancelled && payment.cancellation_reason && (
            <div>
              <span className="text-sm text-gray-500">Cancellation Reason</span>
              <p className="mt-1 text-sm bg-red-50 p-2 rounded text-red-700">{payment.cancellation_reason}</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CancelPaymentModal = ({ payment, isOpen, onClose, onConfirm, isLoading }) => {
  const [reason, setReason] = useState('');
  
  const handleConfirm = () => {
    onConfirm(payment.id, reason);
    setReason('');
  };
  
  if (!payment) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="cancel-payment-modal">
        <DialogHeader>
          <DialogTitle>Cancel Payment</DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel this payment? This will restore £{payment.amount?.toFixed(2)} to {payment.customer_name}'s balance.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Cancellation Reason (optional)</label>
            <Input
              placeholder="Enter reason..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1"
              data-testid="cancel-reason-input"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Keep Payment
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm}
            disabled={isLoading}
            data-testid="confirm-cancel-btn"
          >
            {isLoading && <Loader2 size={16} className="mr-2 animate-spin" />}
            Cancel Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [summary, setSummary] = useState(null);
  
  // Modal states
  const [viewPayment, setViewPayment] = useState(null);
  const [cancelPayment, setCancelPayment] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('streakfree_token');
      
      let url = `${API}/api/payments?limit=200`;
      if (selectedMethod !== 'all') {
        url += `&payment_method=${selectedMethod}`;
      }
      if (selectedStatus !== 'all') {
        url += `&status=${selectedStatus}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [selectedMethod, selectedStatus]);

  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API}/api/payments/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchSummary();
  }, [fetchPayments, fetchSummary]);

  const handleCancelPayment = async (paymentId, reason) => {
    try {
      setIsCancelling(true);
      const token = localStorage.getItem('streakfree_token');
      
      const response = await fetch(`${API}/api/payments/${paymentId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        toast.success('Payment cancelled successfully');
        setCancelPayment(null);
        fetchPayments();
        fetchSummary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel payment');
      }
    } catch (error) {
      console.error('Error cancelling payment:', error);
      toast.error('Failed to cancel payment');
    } finally {
      setIsCancelling(false);
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      payment.customer_name?.toLowerCase().includes(query) ||
      payment.reference?.toLowerCase().includes(query) ||
      payment.notes?.toLowerCase().includes(query)
    );
  });

  const totalReceived = filteredPayments
    .filter(p => p.status !== 'cancelled')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const paymentMethods = [
    { value: 'all', label: 'All Methods' },
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'card', label: 'Card' },
    { value: 'stripe', label: 'Stripe' },
    { value: 'gocardless', label: 'GoCardless' },
    { value: 'direct_debit', label: 'Direct Debit' },
    { value: 'bacs', label: 'BACS' },
    { value: 'standing_order', label: 'Standing Order' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="h-full flex flex-col" data-testid="payments-page">
      {/* Summary Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4">
        <p className="text-sm opacity-80">Total Received</p>
        <p className="text-3xl font-bold" data-testid="total-received">
          £{totalReceived.toFixed(2)}
        </p>
        {summary && (
          <p className="text-sm opacity-80 mt-1">
            {summary.payment_count} payments
          </p>
        )}
      </div>
      
      {/* Search & Filters */}
      <div className="p-4 bg-white border-b border-gray-200 space-y-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search payments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-payments-input"
            />
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => { fetchPayments(); fetchSummary(); }}
            data-testid="refresh-payments-btn"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        
        {/* Filter Pills */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex-shrink-0">
                <Filter size={14} className="mr-1" />
                {paymentMethods.find(m => m.value === selectedMethod)?.label || 'All Methods'}
                <ChevronDown size={14} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {paymentMethods.map(method => (
                <DropdownMenuItem
                  key={method.value}
                  onClick={() => setSelectedMethod(method.value)}
                >
                  {method.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex-shrink-0">
                <ArrowUpDown size={14} className="mr-1" />
                {selectedStatus === 'all' ? 'All Status' : selectedStatus === 'completed' ? 'Completed' : 'Cancelled'}
                <ChevronDown size={14} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setSelectedStatus('all')}>All Status</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedStatus('completed')}>Completed</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedStatus('cancelled')}>Cancelled</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Payments Count */}
      <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600 flex justify-between items-center">
        <span>{filteredPayments.length} payments</span>
        {selectedMethod !== 'all' || selectedStatus !== 'all' ? (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { setSelectedMethod('all'); setSelectedStatus('all'); }}
          >
            <X size={14} className="mr-1" />
            Clear filters
          </Button>
        ) : null}
      </div>
      
      {/* Payments List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="animate-spin text-gray-400" size={32} />
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-500">
            <PoundSterling size={48} className="text-gray-300 mb-2" />
            <p>No payments found</p>
            {(searchQuery || selectedMethod !== 'all' || selectedStatus !== 'all') && (
              <p className="text-sm">Try adjusting your filters</p>
            )}
          </div>
        ) : (
          filteredPayments.map(payment => (
            <PaymentCard 
              key={payment.id} 
              payment={payment}
              onView={setViewPayment}
              onCancel={setCancelPayment}
            />
          ))
        )}
      </div>
      
      {/* Modals */}
      <PaymentDetailModal
        payment={viewPayment}
        isOpen={!!viewPayment}
        onClose={() => setViewPayment(null)}
      />
      
      <CancelPaymentModal
        payment={cancelPayment}
        isOpen={!!cancelPayment}
        onClose={() => setCancelPayment(null)}
        onConfirm={handleCancelPayment}
        isLoading={isCancelling}
      />
    </div>
  );
};

export default Payments;
